import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  FaShippingFast, 
  FaTicketAlt, 
  FaUser, 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaBox, 
  FaWeight, 
  FaBarcode, 
  FaDownload, 
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPaperPlane
} from 'react-icons/fa';
import './UserParameter.css'; // Reusing existing styles

const ColissimoLabels = ({ prefilledTicketId = '' }) => {
  const { t } = useTranslation();
  const [ticketId, setTicketId] = useState(prefilledTicketId);
  const [orderNumber, setOrderNumber] = useState('');
  const [generating, setGenerating] = useState(false);
  const [fetchingSenderData, setFetchingSenderData] = useState(false);
  const [fetchingOrderData, setFetchingOrderData] = useState(false);
  const [sendingAsAttachment, setSendingAsAttachment] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('');
  const [formData, setFormData] = useState({
    customer_first_name: '',
    customer_last_name: '',
    customer_company_name: '',
    customer_line1: '',
    customer_line2: '',
    customer_city: '',
    customer_zip_code: '',
    customer_country_code: 'FR',
    customer_phone_number: '',
    customer_email: '',
    order_number: '',
    weight: 0.5,
    product_code: 'CORE',
    insurance_value_euros: ''
  });
  const [generatedLabel, setGeneratedLabel] = useState(null);

  // Update ticketId when prefilledTicketId prop changes
  useEffect(() => {
    if (prefilledTicketId) {
      setTicketId(prefilledTicketId);
    }
  }, [prefilledTicketId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTicketIdChange = (e) => {
    setTicketId(e.target.value);
  };

  const handleOrderNumberChange = (e) => {
    setOrderNumber(e.target.value);
  };

  const fetchCustomerDataByOrder = async () => {
    if (!orderNumber.trim()) {
      showPopup(t('colissimoLabels.pleaseEnterOrderNumber'), 'error');
      return;
    }

    try {
      setFetchingOrderData(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`/api/colissimo/order/${orderNumber.trim()}/customer-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.success && response.data.order_data) {
        const orderData = response.data.order_data;
        
        // Map order data to form fields
        const updatedFormData = {
          ...formData,
          customer_first_name: orderData.prenom || orderData.firstName || '',
          customer_last_name: orderData.nom || orderData.lastName || '',
          customer_company_name: orderData.societe || orderData.company || '',
          customer_line1: orderData.adresse || orderData.address || orderData.rue || '',
          customer_line2: orderData.adresse2 || orderData.address2 || orderData.complement || '',
          customer_city: orderData.ville || orderData.city || '',
          customer_zip_code: orderData.code_postal || orderData.zipCode || orderData.cp || '',
          customer_country_code: orderData.pays || orderData.country || 'FR',
          customer_phone_number: orderData.telephone || orderData.phone || orderData.tel || '',
          customer_email: orderData.email || orderData.mail || '',
          order_number: String(response.data.num_commande || orderNumber.trim())
        };
        
        setFormData(updatedFormData);

        // Force form validation by directly setting values and triggering events
        setTimeout(() => {
          const formInputs = document.querySelectorAll('#colissimo-form input, #colissimo-form select');
          formInputs.forEach(input => {
            const fieldName = input.name;
            const fieldValue = updatedFormData[fieldName];
            
            if (fieldValue !== undefined && fieldValue !== '') {
              input.value = fieldValue;
              
              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
              const nativeSelectValueSetter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, "value").set;
              
              if (input.tagName === 'SELECT' && nativeSelectValueSetter) {
                nativeSelectValueSetter.call(input, fieldValue);
              } else if (nativeInputValueSetter) {
                nativeInputValueSetter.call(input, fieldValue);
              }
              
              const event = new Event('input', { bubbles: true });
              input.dispatchEvent(event);
              
              const changeEvent = new Event('change', { bubbles: true });
              input.dispatchEvent(changeEvent);
              
              input.setCustomValidity('');
            }
          });
          
          const orderNumberInput = document.getElementById('order_number');
          if (orderNumberInput && updatedFormData.order_number) {
            orderNumberInput.focus();
            orderNumberInput.blur();
          }
        }, 150);

        showPopup(t('colissimoLabels.customerInfoLoadedFromOrder'), 'success');
      } else {
        showPopup(t('colissimoLabels.noCustomerDataForOrder'), 'error');
      }
    } catch (error) {
      // Status only: the error object carries the request (order number, token) and the answer (KI-215).
      console.error('Customer data by order failed:', error.response?.status);
      showPopup(error.response?.data?.detail || t('colissimoLabels.errorFetchingCustomerFromOrder'), 'error');
    } finally {
      setFetchingOrderData(false);
    }
  };

  const fetchSenderAddress = async () => {
    if (!ticketId.trim()) {
      showPopup(t('colissimoLabels.pleaseEnterTicketId'), 'error');
      return;
    }

    try {
      setFetchingSenderData(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`/api/colissimo/ticket/${ticketId.trim()}/sender-address`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.sender_address) {
        const senderAddress = response.data.sender_address;
        
        // Update form data with fetched sender address
        const updatedFormData = {
          ...formData,
          customer_first_name: senderAddress.first_name || '',
          customer_last_name: senderAddress.last_name || '',
          customer_company_name: senderAddress.company_name || '',
          customer_line1: senderAddress.line1 || '',
          customer_line2: senderAddress.line2 || '',
          customer_city: senderAddress.city || '',
          customer_zip_code: senderAddress.zip_code || '',
          customer_country_code: senderAddress.country_code || 'FR',
          customer_phone_number: senderAddress.phone_number || '',
          customer_email: senderAddress.email || '',
          order_number: String(response.data.order_reference || response.data.tracking_number || '')
        };
        
        setFormData(updatedFormData);

        // Force form validation by directly setting values and triggering events
        setTimeout(() => {
          const formInputs = document.querySelectorAll('#colissimo-form input, #colissimo-form select');
          formInputs.forEach(input => {
            // Get the corresponding value from updatedFormData
            const fieldName = input.name;
            const fieldValue = updatedFormData[fieldName];
            
            if (fieldValue !== undefined && fieldValue !== '') {
              // Set the value directly on the DOM element
              input.value = fieldValue;
              
              // Create and dispatch events that React will recognize
              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
              const nativeSelectValueSetter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, "value").set;
              
              if (input.tagName === 'SELECT' && nativeSelectValueSetter) {
                nativeSelectValueSetter.call(input, fieldValue);
              } else if (nativeInputValueSetter) {
                nativeInputValueSetter.call(input, fieldValue);
              }
              
              // Trigger React's onChange event
              const event = new Event('input', { bubbles: true });
              input.dispatchEvent(event);
              
              // Also trigger change event for good measure
              const changeEvent = new Event('change', { bubbles: true });
              input.dispatchEvent(changeEvent);
              
              // Mark as valid
              input.setCustomValidity('');
            }
          });
          
          // Special handling for the order number field to ensure it's properly validated
          const orderNumberInput = document.getElementById('order_number');
          if (orderNumberInput && updatedFormData.order_number) {
            orderNumberInput.focus();
            orderNumberInput.blur();
          }
        }, 150);

        showPopup(t('colissimoLabels.customerInfoLoaded'), 'success');
      } else {
        showPopup(t('colissimoLabels.noSenderAddressForTicket'), 'error');
      }
    } catch (error) {
      console.error('Sender address failed:', error.response?.status);
      showPopup(error.response?.data?.detail || t('colissimoLabels.errorFetchingCustomerInfo'), 'error');
    } finally {
      setFetchingSenderData(false);
    }
  };

  const generateLabel = async (e) => {
    e.preventDefault();
    
    if (!ticketId.trim()) {
      showPopup(t('colissimoLabels.pleaseEnterTicketId'), 'error');
      return;
    }

    try {
      setGenerating(true);
      const token = localStorage.getItem('access_token');
      
      const requestData = {
        ticket_id: ticketId.trim(),
        ...formData,
        insurance_value_euros: formData.insurance_value_euros ? parseFloat(formData.insurance_value_euros) : null
      };

      const response = await axios.post('/api/colissimo/return-label', requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setGeneratedLabel(response.data);
        showPopup(t('colissimoLabels.labelGeneratedSuccess'), 'success');
      } else {
        showPopup(response.data.error || t('colissimoLabels.failedToGenerateLabel'), 'error');
      }
    } catch (error) {
      console.error('Label generation failed:', error.response?.status);
      showPopup(error.response?.data?.detail || t('colissimoLabels.errorGeneratingLabel'), 'error');
    } finally {
      setGenerating(false);
    }
  };

  const downloadLabel = () => {
    if (generatedLabel?.presigned_url) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = generatedLabel.presigned_url;
      link.download = generatedLabel.filename || 'colissimo-label.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const sendLabelAsAttachment = async () => {
    if (!ticketId) {
      showPopup(t('colissimoLabels.unableToSendLabel'), 'error');
      return;
    }

    setSendingAsAttachment(true);
    try {
      const token = localStorage.getItem('access_token');

      // Use the backend endpoint to send the label as attachment
      const response = await axios.post(
        `/api/colissimo/ticket/${ticketId}/send-label-as-attachment`,
        {},
        {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        showPopup(response.data.message || t('colissimoLabels.labelSentSuccess'), 'success');
      } else {
        showPopup(t('colissimoLabels.failedToSendLabel'), 'error');
      }
    } catch (error) {
      console.error('Sending the label failed:', error.response?.status);
      showPopup(error.response?.data?.detail || t('colissimoLabels.failedToSendLabel'), 'error');
    } finally {
      setSendingAsAttachment(false);
    }
  };

  const showPopup = (message, type) => {
    setPopupMessage(message);
    setPopupType(type);
    setTimeout(() => {
      setPopupMessage('');
      setPopupType('');
    }, 5000);
  };

  return (
    <div className="user-parameter-container">
      <div className="user-parameter-content">
        {/* Header */}
        <div className="section-title">
          <FaShippingFast />
          {t('colissimoLabels.title')}
        </div>

        {/* Popup Messages */}
        {popupMessage && (
          <div className={`popup-message ${popupType}`} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-4)',
            borderRadius: '8px',
            marginBottom: 'var(--space-4)',
            fontWeight: '500',
            ...(popupType === 'success' ? {
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              color: '#10b981'
            } : {
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#ef4444'
            })
          }}>
            {popupType === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
            {popupMessage}
          </div>
        )}

        {/* Main Form */}
        <div className="settings-card">
          <form id="colissimo-form" onSubmit={generateLabel}>
            {/* Ticket ID Input */}
            <div className="form-section">
              <div className="section-subtitle">
                <FaTicketAlt />
                {t('colissimoLabels.ticketInformation')}
              </div>
              
              <div className="form-group">
                <label htmlFor="ticket_id">
                  <FaTicketAlt /> {t('colissimoLabels.ticketId')}
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                  <input
                    type="text"
                    id="ticket_id"
                    name="ticket_id"
                    value={ticketId}
                    onChange={handleTicketIdChange}
                    placeholder={t('colissimoLabels.enterTicketId')}
                    required
                    style={{ flex: 1 }}
                  />
                  <button 
                    type="button" 
                    onClick={fetchSenderAddress}
                    disabled={fetchingSenderData || !ticketId.trim()}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: fetchingSenderData || !ticketId.trim() ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      fontSize: 'var(--font-size-body-sm)',
                      opacity: fetchingSenderData || !ticketId.trim() ? 0.6 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {fetchingSenderData ? (
                      <>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                        {t('colissimoLabels.loading')}
                      </>
                    ) : (
                      <>
                        <FaUser />
                        {t('colissimoLabels.loadCustomer')}
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 'var(--space-4)' }}>
                <label htmlFor="order_number_lookup">
                  <FaBarcode /> {t('colissimoLabels.orLoadByOrderNumber')}
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                  <input
                    type="text"
                    id="order_number_lookup"
                    name="order_number_lookup"
                    value={orderNumber}
                    onChange={handleOrderNumberChange}
                    placeholder={t('colissimoLabels.enterOrderNumber')}
                    style={{ flex: 1 }}
                  />
                  <button 
                    type="button" 
                    onClick={fetchCustomerDataByOrder}
                    disabled={fetchingOrderData || !orderNumber.trim()}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: fetchingOrderData || !orderNumber.trim() ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      fontSize: 'var(--font-size-body-sm)',
                      opacity: fetchingOrderData || !orderNumber.trim() ? 0.6 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {fetchingOrderData ? (
                      <>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                        {t('colissimoLabels.loading')}
                      </>
                    ) : (
                      <>
                        <FaBarcode />
                        {t('colissimoLabels.loadByOrder')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="form-section">
              <div className="section-subtitle">
                <FaUser />
                {t('colissimoLabels.customerInformation')}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customer_first_name">
                    <FaUser /> {t('colissimoLabels.firstName')}
                  </label>
                  <input
                    type="text"
                    id="customer_first_name"
                    name="customer_first_name"
                    value={formData.customer_first_name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="customer_last_name">
                    <FaUser /> {t('colissimoLabels.lastName')}
                  </label>
                  <input
                    type="text"
                    id="customer_last_name"
                    name="customer_last_name"
                    value={formData.customer_last_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="customer_company_name">
                  <FaBuilding /> {t('colissimoLabels.companyNameOptional')}
                </label>
                <input
                  type="text"
                  id="customer_company_name"
                  name="customer_company_name"
                  value={formData.customer_company_name}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="customer_line1">
                  <FaMapMarkerAlt /> {t('colissimoLabels.addressLine1')}
                </label>
                <input
                  type="text"
                  id="customer_line1"
                  name="customer_line1"
                  value={formData.customer_line1}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="customer_line2">
                  <FaMapMarkerAlt /> {t('colissimoLabels.addressLine2Optional')}
                </label>
                <input
                  type="text"
                  id="customer_line2"
                  name="customer_line2"
                  value={formData.customer_line2}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customer_city">
                    <FaMapMarkerAlt /> {t('colissimoLabels.city')}
                  </label>
                  <input
                    type="text"
                    id="customer_city"
                    name="customer_city"
                    value={formData.customer_city}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="customer_zip_code">
                    <FaMapMarkerAlt /> {t('colissimoLabels.zipCode')}
                  </label>
                  <input
                    type="text"
                    id="customer_zip_code"
                    name="customer_zip_code"
                    value={formData.customer_zip_code}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customer_country_code">
                    <FaMapMarkerAlt /> {t('colissimoLabels.countryCode')}
                  </label>
                  <select
                    id="customer_country_code"
                    name="customer_country_code"
                    value={formData.customer_country_code}
                    onChange={handleInputChange}
                  >
                    <option value="FR">France (FR)</option>
                    <option value="BE">Belgium (BE)</option>
                    <option value="DE">Germany (DE)</option>
                    <option value="ES">Spain (ES)</option>
                    <option value="IT">Italy (IT)</option>
                    <option value="NL">Netherlands (NL)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="customer_phone_number">
                    <FaPhone /> {t('colissimoLabels.phoneNumber')}
                  </label>
                  <input
                    type="tel"
                    id="customer_phone_number"
                    name="customer_phone_number"
                    value={formData.customer_phone_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="customer_email">
                  <FaEnvelope /> {t('colissimoLabels.email')}
                </label>
                <input
                  type="email"
                  id="customer_email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Package Information */}
            <div className="form-section">
              <div className="section-subtitle">
                <FaBox />
                {t('colissimoLabels.packageInformation')}
              </div>

              <div className="form-group">
                <label htmlFor="order_number">
                  <FaBarcode /> {t('colissimoLabels.orderNumber')}
                </label>
                <input
                  type="text"
                  id="order_number"
                  name="order_number"
                  value={formData.order_number}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="weight">
                    <FaWeight /> {t('colissimoLabels.weightKg')}
                  </label>
                  <input
                    type="number"
                    id="weight"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    min="0.1"
                    max="30"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="product_code">
                    <FaBox /> {t('colissimoLabels.productCode')}
                  </label>
                  <select
                    id="product_code"
                    name="product_code"
                    value={formData.product_code}
                    onChange={handleInputChange}
                  >
                    <option value="CORE">CORE</option>
                    <option value="CORI">CORI</option>
                    <option value="COLR">COLR</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="insurance_value_euros">
                  <FaBox /> {t('colissimoLabels.insuranceValueOptional')}
                </label>
                <input
                  type="number"
                  id="insurance_value_euros"
                  name="insurance_value_euros"
                  value={formData.insurance_value_euros}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder={t('colissimoLabels.leaveEmptyNoInsurance')}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button 
                type="submit" 
                className="primary-button"
                disabled={generating}
              >
                {generating ? (
                  <>
                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> {t('colissimoLabels.generating')}
                  </>
                ) : (
                  <>
                    <FaShippingFast /> {t('colissimoLabels.generateReturnLabel')}
                  </>
                )}
              </button>
            </div>

            {/* Generated Label Result */}
            {generatedLabel && (
              <div className="form-section">
                <div className="section-subtitle">
                  <FaCheckCircle />
                  {t('colissimoLabels.generatedLabel')}
                </div>
                
                <div style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '8px',
                  padding: 'var(--space-4)',
                  marginTop: 'var(--space-4)'
                }}>
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <div style={{ marginBottom: 'var(--space-2)', color: 'var(--text-primary)', fontSize: 'var(--font-size-body-sm)' }}>
                      <strong>{t('colissimoLabels.trackingNumber')}</strong> {generatedLabel.tracking_number}
                    </div>
                    <div style={{ marginBottom: 'var(--space-2)', color: 'var(--text-primary)', fontSize: 'var(--font-size-body-sm)' }}>
                      <strong>{t('colissimoLabels.filename')}</strong> {generatedLabel.filename}
                    </div>
                    {generatedLabel.message && (
                      <div style={{ marginBottom: 'var(--space-2)', color: 'var(--text-primary)', fontSize: 'var(--font-size-body-sm)' }}>
                        <strong>{t('colissimoLabels.message')}</strong> {generatedLabel.message}
                      </div>
                    )}
                  </div>
                  
                  <div className="form-actions" style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <button 
                      type="button" 
                      className="primary-button"
                      onClick={downloadLabel}
                    >
                      <FaDownload /> {t('colissimoLabels.downloadLabel')}
                    </button>
                    <button 
                      type="button" 
                      className="primary-button"
                      onClick={sendLabelAsAttachment}
                      disabled={sendingAsAttachment}
                      style={{
                        opacity: sendingAsAttachment ? 0.6 : 1,
                        cursor: sendingAsAttachment ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {sendingAsAttachment ? (
                        <>
                          <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> {t('colissimoLabels.sending')}
                        </>
                      ) : (
                        <>
                          <FaPaperPlane /> {t('colissimoLabels.sendAsAttachment')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .form-section {
          margin-bottom: var(--space-6);
          padding-bottom: var(--space-5);
          border-bottom: 1px solid var(--border-subtle);
        }
        
        .form-section:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }
        
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-4);
        }
      `}</style>
    </div>
  );
};

export default ColissimoLabels;
